import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { MarketingNav } from "../components/Nav";
import Footer from "../components/Footer";

const sections = [
  {
    id: "intro",
    title: "Introduction",
    body: `Bora Moyo ("we", "our", or "us") is committed to protecting your personal information and your right to privacy. This Privacy Policy explains what information we collect, how we use it, and what rights you have in relation to it.

By using Bora Moyo, you agree to the collection and use of information in accordance with this policy. If you do not agree, please do not use the service.`,
  },
  {
    id: "collect",
    title: "Information we collect",
    body: `We collect information you provide directly, including:
• Account information — name, email address, date of birth, and profile details you choose to share.
• Profile content — photos, written prompts, voice or video introductions you upload.
• Preferences — your match criteria, compatibility weights, and round decisions.
• Communications — messages exchanged with matches within the platform.
• Payment information — processed securely by our payment provider. We do not store card numbers.

We also collect information automatically:
• Device and usage data — device type, browser, IP address, pages visited, and time spent.
• Cookies and similar technologies — used for session management and performance analytics.`,
  },
  {
    id: "use",
    title: "How we use your information",
    body: `We use your information to:
• Provide, operate, and improve the Bora Moyo service.
• Match you with compatible prospects according to your stated preferences.
• Send you notifications, updates, and support communications.
• Ensure platform safety, detect abuse, and enforce our policies.
• Comply with legal obligations.

We do not sell your personal data to third parties. We do not use your data for advertising networks outside of Bora Moyo.`,
  },
  {
    id: "sharing",
    title: "Sharing your information",
    body: `We share limited information with:
• Other users — only what you choose to include in your profile and what you share in rounds and messages.
• Service providers — trusted vendors who help us operate the platform (hosting, analytics, payment processing), bound by confidentiality agreements.
• Legal authorities — when required by law or to protect the rights, safety, and property of Bora Moyo, our members, and others.

We do not share your information with advertisers.`,
  },
  {
    id: "retention",
    title: "Data retention",
    body: `We retain your data for as long as your account is active or as needed to provide services. You may delete your account at any time from Settings. Upon deletion, your profile, messages, and personal data are removed within 30 days, except where retention is required by law.`,
  },
  {
    id: "rights",
    title: "Your rights",
    body: `Depending on your location, you may have the right to:
• Access the personal data we hold about you.
• Correct inaccurate data.
• Request deletion of your data.
• Object to or restrict processing.
• Data portability.
• Lodge a complaint with a supervisory authority.

To exercise any of these rights, contact us at privacy@boramoyo.com.`,
  },
  {
    id: "security",
    title: "Security",
    body: `We use industry-standard security measures including encryption in transit (TLS) and at rest, access controls, and regular security reviews. No method of transmission over the internet is 100% secure, but we take your data security seriously and work continuously to improve our practices.`,
  },
  {
    id: "children",
    title: "Children's privacy",
    body: `Bora Moyo is not intended for anyone under the age of 18. We do not knowingly collect personal data from minors. If we become aware that a user is under 18, we will terminate their account and delete their data promptly.`,
  },
  {
    id: "changes",
    title: "Changes to this policy",
    body: `We may update this policy from time to time. We will notify you of significant changes by email or via in-app notice. Continued use of Bora Moyo after changes take effect constitutes acceptance of the updated policy.`,
  },
  {
    id: "contact",
    title: "Contact",
    body: `For privacy-related questions or requests:\nEmail: privacy@boramoyo.com\nBora Moyo Privacy Team\nLast updated: April 2026`,
  },
];

export default function Privacy() {
  useEffect(() => {
    document.title = "Privacy Policy — Bora Moyo";
  }, []);

  return (
    <div className="bg-[#05070d] min-h-screen text-white">
      <MarketingNav />

      <section className="pt-40 pb-16 md:pt-52 px-6 md:px-12">
        <div className="mx-auto max-w-3xl">
          <div className="text-[11px] tracking-[0.28em] uppercase text-[#D4AF37] mb-4">Legal</div>
          <h1 className="font-display text-4xl md:text-6xl leading-[1.04] tracking-tight mb-6">Privacy Policy</h1>
          <p className="text-white/50 text-sm">Last updated: April 2026 &middot; Effective immediately</p>
        </div>
      </section>

      <section className="pb-24 px-6 md:px-12">
        <div className="mx-auto max-w-3xl">
          {/* TOC */}
          <nav className="backdrop-blur-xl bg-[#0C111D]/65 border border-white/8 rounded-2xl p-6 mb-10">
            <div className="text-[11px] tracking-[0.22em] uppercase text-[#D4AF37] mb-4">Contents</div>
            <ol className="space-y-1.5 text-sm text-white/60">
              {sections.map((s, i) => (
                <li key={s.id}>
                  <a href={`#${s.id}`} className="hover:text-white transition-colors">
                    {i + 1}. {s.title}
                  </a>
                </li>
              ))}
            </ol>
          </nav>

          <div className="space-y-10">
            {sections.map((s) => (
              <div key={s.id} id={s.id} className="scroll-mt-24">
                <h2 className="font-display text-2xl mb-4">{s.title}</h2>
                <div className="text-white/60 text-sm leading-relaxed whitespace-pre-line">{s.body}</div>
                <div className="h-px bg-white/5 mt-10" />
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <Link to="/contact" className="text-sm text-[#D4AF37] hover:text-[#F2DE94] transition-colors">
              Questions about this policy? Contact us →
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
